import { useRef, useState, useEffect, useCallback, useMemo } from "react";

/** Shared getUserMedia camera hook for Camera Vision, Voice Orb live vision,
 *  and People Memory face capture. Supports flipping between the rear
 *  ("environment") and front ("user") cameras on phones/tablets - critical on
 *  iPhone and iPad where face enrollment obviously wants the front cam while
 *  general scene vision wants the rear cam, and Doc needs both accessible. */
export function useCamera(initialFacing = "environment") {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [active, setActive] = useState(false);
  const [error, setError] = useState("");
  const [facing, setFacing] = useState(initialFacing); // "environment" | "user"

  const _openStream = useCallback(async (mode) => {
    // Try the preferred camera first with an ideal constraint; if that fails
    // (e.g. on desktop-only browsers where "environment" doesn't map to any
    // device) fall back to the other side, then to any camera at all.
    const attempts = [
      { video: { facingMode: { ideal: mode }, width: { ideal: 960 } } },
      { video: { facingMode: mode, width: { ideal: 960 } } },
      { video: { facingMode: mode === "environment" ? "user" : "environment", width: { ideal: 960 } } },
      { video: true },
    ];
    let lastErr = null;
    for (const constraints of attempts) {
      try {
        return await navigator.mediaDevices.getUserMedia(constraints);
      } catch (e) {
        lastErr = e;
      }
    }
    throw lastErr || new Error("No camera available");
  }, []);

  const start = useCallback(async (facingOverride) => {
    const mode = facingOverride || facing;
    try {
      // Tear down any previously-open stream before opening a new one so we
      // don't leak a rear-cam handle when switching to the front cam on iOS.
      streamRef.current?.getTracks().forEach((t) => t.stop());
      const stream = await _openStream(mode);
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        // iOS Safari requires playsInline (set on the <video> element) plus
        // an explicit .play() call - .play() returns a promise we must await
        // or camera preview silently stays black.
        await videoRef.current.play();
      }
      setActive(true);
      setError("");
      if (facingOverride) setFacing(facingOverride);
    } catch (e) {
      // Distinguish denied-permission (recoverable - Doc grants and retries)
      // from no-camera-hardware (dead-end - surface distinctly to the UI).
      const name = e?.name || "";
      if (name === "NotAllowedError" || name === "SecurityError") {
        setError("Camera permission blocked. Enable it in your browser settings and try again.");
      } else if (name === "NotFoundError" || name === "OverconstrainedError") {
        setError("No matching camera on this device.");
      } else if (name === "NotReadableError") {
        setError("Camera busy - another app is using it. Close and try again.");
      } else {
        setError("Camera access failed on this device.");
      }
    }
  }, [facing, _openStream]);

  const stop = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setActive(false);
  }, []);

  // Flip the camera without tearing down the consumer UI. On mobile this is
  // what lets Doc toggle between front (for face enrollment / greeting) and
  // rear (for scene vision / OCR / object recognition) with one tap.
  const flip = useCallback(async () => {
    const next = facing === "environment" ? "user" : "environment";
    setFacing(next);
    await start(next);
  }, [facing, start]);

  const capture = useCallback(() => {
    if (!videoRef.current || !videoRef.current.videoWidth) return null;
    const canvas = document.createElement("canvas");
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    canvas.getContext("2d").drawImage(videoRef.current, 0, 0);
    return canvas.toDataURL("image/jpeg", 0.85);
  }, []);

  useEffect(() => () => stop(), [stop]);

  // MUST be memoized: a fresh object literal every render gave every consumer callback a
  // new identity each render - in RealtimeVoiceOrb that chain reached the unmount-cleanup
  // effect, which then called disconnect() on EVERY re-render, killing the live voice
  // session the instant it started connecting ("orb comes on then goes off" bug).
  return useMemo(
    () => ({ videoRef, active, error, facing, start, stop, capture, flip }),
    [active, error, facing, start, stop, capture, flip]
  );
}
